import { use, useCallback } from 'react';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { PracticeVideoContext } from './practice_video/practice_video_context.tsx';

/**
 * Whether the player's mixer card is open: the stored choice, except that video mode (GitHub issue #4041) keeps
 * the card open, since the video lives in it and the mixer button is gone. Closing it is ignored in video mode
 * and the stored choice is kept for when video mode ends.
 */
export function useMixerVisibility(): {
  isMixerVisible: boolean;
  setIsMixerVisible: (visible: boolean | ((isMixerVisible: boolean) => boolean)) => void;
} {
  const [storedIsMixerVisible, setStoredIsMixerVisible] = useLocalStorage(LocalStorageKey.IsMixerVisible);
  const { isVideoMode } = use(PracticeVideoContext);

  const setIsMixerVisible = useCallback(
    (visible: boolean | ((isMixerVisible: boolean) => boolean)) => {
      setStoredIsMixerVisible((stored) => {
        const next = typeof visible === 'function' ? visible(stored || isVideoMode) : visible;
        return isVideoMode && !next ? stored : next;
      });
    },
    [isVideoMode, setStoredIsMixerVisible],
  );

  return { isMixerVisible: storedIsMixerVisible || isVideoMode, setIsMixerVisible };
}
